<script setup>
import { computed, inject, ref, onBeforeUnmount, onMounted } from 'vue';
import { formatNumber } from '@chatwoot/utils';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ZoraxyPipButton from './ZoraxyPipButton.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeAssigneeTab: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  isFloatingHeader: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'openArchived',
  'resetFilters',
  'filtersModal',
  'enterSelectionMode',
  'openStarredMessages',
  'newConversation',
]);

const headerMenuOpen = ref(false);
const headerMenuRef = ref(null);
const mobileSidebarLabel = 'Menu lateral';
const concludedButtonLabel = 'Concluídos';
const toggleMobileSidebar = inject('toggleMobileSidebar', null);
const shouldShowMobileSidebarButton = computed(() => {
  return typeof toggleMobileSidebar === 'function';
});

const onClickMobileSidebar = () => {
  toggleMobileSidebar?.();
};

const toggleHeaderMenu = () => {
  headerMenuOpen.value = !headerMenuOpen.value;
};
const closeHeaderMenu = () => {
  headerMenuOpen.value = false;
};
const onHeaderMenuDocClick = e => {
  if (!headerMenuOpen.value) return;
  if (headerMenuRef.value && !headerMenuRef.value.contains(e.target)) {
    closeHeaderMenu();
  }
};
onMounted(() => document.addEventListener('click', onHeaderMenuDocClick));
onBeforeUnmount(() =>
  document.removeEventListener('click', onHeaderMenuDocClick)
);
const onClickSelectMode = () => {
  closeHeaderMenu();
  emit('enterSelectionMode');
};
const onClickStarredMessages = () => {
  closeHeaderMenu();
  emit('openStarredMessages');
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));
</script>

<template>
  <div
    class="acl-chatlist-header flex items-center justify-between gap-2"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
      'acl-chatlist-header--floating': isFloatingHeader,
    }"
  >
    <button
      v-if="shouldShowMobileSidebarButton"
      id="mobile-sidebar-launcher"
      v-tooltip.bottom="mobileSidebarLabel"
      data-mobile-sidebar-launcher
      type="button"
      class="acl-mobile-sidebar-btn"
      :aria-label="mobileSidebarLabel"
      @click.stop="onClickMobileSidebar"
    >
      <span class="i-lucide-menu" data-mobile-sidebar-launcher />
    </button>
    <div class="acl-chatlist-heading min-w-0">
      <div class="flex items-center min-w-0">
        <h1 class="acl-chatlist-title truncate" :title="pageTitle">
          {{ pageTitle }}
        </h1>
        <span
          v-if="
            allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
          "
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
          :title="allCount"
        >
          {{ formattedAllCount }}
        </span>
      </div>
    </div>
    <div class="acl-chatlist-actions flex items-center gap-1">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="acl-conversation-filter-target acl-conversation-filter-target--anchored"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-if="!hasActiveFolders" class="relative">
        <div
          id="conversationFilterTeleportTarget"
          class="acl-conversation-filter-target acl-conversation-filter-target--standalone"
        />
      </div>
      <ZoraxyPipButton class="acl-pip-launcher" />
      <button
        v-tooltip.bottom-end="$t('CHAT_LIST.NEW_CONVERSATION')"
        type="button"
        class="acl-new-conv-btn"
        :aria-label="$t('CHAT_LIST.NEW_CONVERSATION')"
        @click="emit('newConversation')"
      >
        <span class="i-lucide-message-square-plus" />
      </button>
      <button
        v-tooltip.bottom-end="concludedButtonLabel"
        type="button"
        class="acl-new-conv-btn acl-archive-btn max-sm:!bg-yellow-400 max-sm:!text-yellow-950"
        :class="{
          'acl-archive-btn--active': activeAssigneeTab === 'concluidos',
        }"
        :aria-label="concludedButtonLabel"
        @click="emit('openArchived')"
      >
        <span class="i-lucide-archive" />
      </button>
      <div ref="headerMenuRef" class="acl-header-menu-wrap">
        <button
          v-tooltip.bottom-end="'Mais opções'"
          type="button"
          class="acl-header-menu-btn"
          @click.stop="toggleHeaderMenu"
        >
          <span class="i-lucide-more-vertical" />
        </button>
        <div v-if="headerMenuOpen" class="acl-header-menu" role="menu">
          <button
            type="button"
            class="acl-header-menu-item"
            @click="onClickSelectMode"
          >
            <span class="i-lucide-check-square acl-header-menu-item-icon" />
            {{ $t('CHAT_LIST.SELECT_CONVERSATIONS') }}
          </button>
          <button
            type="button"
            class="acl-header-menu-item"
            @click="onClickStarredMessages"
          >
            <span class="i-lucide-star acl-header-menu-item-icon" />
            {{ $t('CHAT_LIST.STARRED.TITLE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.acl-chatlist-header {
  height: 64px;
  padding-block-start: 10px;
  padding-block-end: 10px;
  padding-inline-start: 20px;
  padding-inline-end: 20px;
}
.acl-chatlist-header--floating {
  position: relative;
  z-index: 30;
  min-height: 64px;
  height: 64px;
  align-items: center;
  padding-block-start: 1.125rem;
  padding-block-end: 1rem;
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 0.96) 0%,
    rgba(248, 250, 252, 0.88) 62%,
    rgba(248, 250, 252, 0) 100%
  );
  backdrop-filter: blur(16px);
}
.acl-chatlist-heading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  gap: 0.35rem;
}
.acl-chatlist-title {
  font-size: 24px;
  font-weight: 600;
  color: #0a0a0a;
  line-height: 1.2;
  margin: 0;
}
.acl-chatlist-subtitle {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #52606d;
}
.acl-mobile-sidebar-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 50%;
  color: #0a0a0a;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background-color 0.12s,
    box-shadow 0.12s,
    transform 0.12s;
}
.acl-mobile-sidebar-btn:hover {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}
.acl-mobile-sidebar-btn:active {
  transform: scale(0.98);
}
.acl-mobile-sidebar-btn > span {
  width: 22px;
  height: 22px;
  background-color: currentColor;
}
.acl-new-conv-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent !important;
  border: none;
  border-radius: 50%;
  color: #0a0a0a;
  cursor: pointer;
  transition: background-color 0.12s;
  flex-shrink: 0;
}
.acl-new-conv-btn:hover {
  background: rgba(10, 10, 10, 0.06) !important;
}
.acl-archive-btn--active,
.acl-archive-btn--active:hover {
  background: rgba(21, 96, 62, 0.12) !important;
  color: #15603e !important;
}
.acl-archive-btn--active > span {
  background-color: #15603e !important;
}
.acl-new-conv-btn > span {
  width: 24px;
  height: 24px;
  background-color: #0a0a0a;
}
:deep(.dark) .acl-new-conv-btn {
  color: #e5e5e5;
}
:deep(.dark) .acl-new-conv-btn > span {
  background-color: #e5e5e5;
}
:deep(.dark) .acl-new-conv-btn:hover {
  background: rgba(229, 229, 229, 0.08) !important;
}
:deep(.dark) .acl-chatlist-header--floating {
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.95) 0%,
    rgba(15, 23, 42, 0.82) 62%,
    rgba(15, 23, 42, 0) 100%
  );
}
:deep(.dark) .acl-chatlist-subtitle {
  color: #cbd5e1;
}
:deep(.dark) .acl-mobile-sidebar-btn {
  background: rgba(31, 33, 35, 0.76);
  border-color: rgba(148, 163, 184, 0.18);
  color: #e5e5e5;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.24);
}
:deep(.dark) .acl-mobile-sidebar-btn:hover {
  background: rgba(42, 45, 48, 0.92);
}
:deep(.dark) .acl-archive-btn--active,
:deep(.dark) .acl-archive-btn--active:hover {
  background: rgba(21, 96, 62, 0.25) !important;
  color: #86efac !important;
}
:deep(.dark) .acl-archive-btn--active > span {
  background-color: #86efac !important;
}
/* Aceleres: 3-dots header menu (WhatsApp-style) */
.acl-header-menu-wrap {
  position: relative;
}
.acl-conversation-filter-target {
  position: absolute;
  z-index: 50;
}
.acl-conversation-filter-target--anchored {
  top: 50%;
  left: calc(100% + 0.625rem);
  transform: translateY(-50%);
}
.acl-conversation-filter-target--standalone {
  top: calc(100% + 0.5rem);
  right: 0;
}
@media (max-width: 1023px) {
  .acl-conversation-filter-target--anchored {
    top: calc(100% + 0.5rem);
    left: auto;
    right: 0;
    transform: none;
  }
}
.acl-header-menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: #0a0a0a;
  cursor: pointer;
  transition: background-color 0.12s;
  flex-shrink: 0;
}
.acl-header-menu-btn:hover {
  background: rgba(10, 10, 10, 0.06);
}
.acl-header-menu-btn > span {
  width: 24px;
  height: 24px;
  background-color: #0a0a0a;
}
.acl-header-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 100;
  padding: 0;
}
.acl-header-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #1a1a1a;
  cursor: pointer;
}
.acl-header-menu-item:hover {
  background-color: #f3f4f6;
}
.acl-header-menu-item-icon {
  width: 16px;
  height: 16px;
  background-color: currentColor;
  flex-shrink: 0;
}
:deep(.dark) .acl-header-menu-btn {
  color: #e5e5e5;
}
:deep(.dark) .acl-header-menu-btn:hover {
  background: rgba(229, 229, 229, 0.08);
}
:deep(.dark) .acl-header-menu-btn > span {
  background-color: #e5e5e5;
}
:deep(.dark) .acl-header-menu {
  background: #1f2123;
  border-color: #2c2f33;
}
:deep(.dark) .acl-header-menu-item {
  color: #e5e5e5;
}
:deep(.dark) .acl-header-menu-item:hover {
  background-color: #2a2d30;
}
@media (max-width: 767px) {
  .acl-chatlist-header,
  .acl-chatlist-header--floating {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    top: env(safe-area-inset-top, 0px) !important;
    bottom: auto !important;
    width: 100% !important;
    min-height: 56px !important;
    height: auto !important;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: 44px;
    align-items: center;
    row-gap: 8px;
    column-gap: 10px;
    padding: calc(6px + env(safe-area-inset-top, 0px)) 12px 6px !important;
    padding-inline: 12px;
    overflow: visible;
    background: rgba(248, 250, 252, 0.98) !important;
    box-shadow: 0 1px 0 rgba(15, 23, 42, 0.08) !important;
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
    z-index: 60 !important;
  }

  .acl-chatlist-heading {
    grid-column: 1;
    grid-row: 1;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    gap: 2px;
  }

  .acl-chatlist-heading > div {
    justify-content: flex-start;
    width: 100%;
  }

  .acl-chatlist-title {
    max-width: calc(100vw - 156px);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.15;
    text-align: left;
  }

  .acl-chatlist-subtitle {
    max-width: 100%;
    font-size: 11px;
    line-height: 1.25;
    text-align: center;
  }

  .acl-chatlist-actions {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    justify-content: flex-end;
    width: 100%;
    min-width: 0;
    gap: 6px;
    overflow-x: auto;
    overflow-y: visible;
    padding-block: 1px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .acl-chatlist-actions::-webkit-scrollbar {
    display: none;
  }

  .acl-chatlist-actions > * {
    flex: 0 0 auto;
  }

  .acl-chatlist-actions :deep(button) {
    min-width: 44px;
    min-height: 44px;
  }

  .acl-conversation-filter-target--anchored,
  .acl-conversation-filter-target--standalone {
    top: calc(100% + 0.375rem);
    right: 0;
    left: auto;
    transform: none;
  }

  .acl-mobile-sidebar-btn {
    display: none !important;
    position: static;
    width: 48px;
    height: 48px;
    background: transparent;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 16px;
    color: #111b21;
    box-shadow: none;
  }

  .acl-mobile-sidebar-btn:hover {
    background: #f8fafc;
  }

  .acl-mobile-sidebar-btn > span {
    width: 28px;
    height: 28px;
    background-color: #111b21;
  }

  .acl-new-conv-btn:not(.acl-archive-btn) {
    display: none !important;
    position: static;
    width: 48px;
    height: 48px;
    background: transparent !important;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 16px;
    color: #111b21;
    box-shadow: none;
  }

  .acl-new-conv-btn:not(.acl-archive-btn):hover {
    background: #f8fafc !important;
  }

  .acl-new-conv-btn:not(.acl-archive-btn) > span {
    width: 28px;
    height: 28px;
    background-color: #111b21;
  }

  .acl-archive-btn,
  .acl-header-menu-btn {
    width: 44px;
    height: 44px;
  }

  .acl-archive-btn {
    display: none !important;
  }

  :deep(.acl-pip-launcher) {
    position: static;
    width: 48px;
    height: 48px;
    padding: 0;
    border: 1px solid rgba(124, 58, 237, 0.16);
    border-radius: 16px;
    background: transparent;
    color: #6d28d9;
    box-shadow: none;
  }

  :deep(.acl-pip-launcher [class*='i-lucide']) {
    width: 28px;
    height: 28px;
    background-color: currentColor;
  }

  :deep(.dark) .acl-chatlist-header {
    background: #111827;
  }

  :deep(.dark) .acl-chatlist-header--floating {
    background: linear-gradient(
      180deg,
      rgba(15, 23, 42, 0.98) 0%,
      rgba(15, 23, 42, 0.9) 78%,
      rgba(15, 23, 42, 0) 100%
    );
  }

  :deep(.dark) .acl-new-conv-btn:not(.acl-archive-btn) {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.12);
    color: #e9edef;
  }

  :deep(.dark) .acl-new-conv-btn:not(.acl-archive-btn) > span {
    background-color: #e9edef;
  }

  :deep(.dark) .acl-mobile-sidebar-btn {
    background: transparent;
    border-color: rgba(255, 255, 255, 0.12);
    color: #e9edef;
  }

  :deep(.dark) .acl-mobile-sidebar-btn:hover {
    background: #ffffff;
  }

  :deep(.dark) .acl-mobile-sidebar-btn > span {
    background-color: #e9edef;
  }

  :deep(.dark) .acl-pip-launcher {
    background: transparent;
    border-color: rgba(167, 139, 250, 0.28);
    color: #c4b5fd;
  }

  :deep(.dark) .acl-pip-launcher:hover {
    background: #35264b;
    color: #ddd6fe;
  }
}
</style>
